import { router } from '@inertiajs/react'
import AppLayout from '@/components/layouts/AppLayout'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardActions from '@mui/material/CardActions'
import Grid from '@mui/material/Grid'
import Avatar from '@mui/material/Avatar'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import PeopleIcon from '@mui/icons-material/People'
import AppsIcon from '@mui/icons-material/Apps'
import type { Vendor, AppEntry } from '@/types'

interface Props {
  vendor: Vendor
  app_entries: AppEntry[]
  current_role: string
}

export default function VendorsShow({ vendor, app_entries, current_role }: Props) {
  const isAdmin = current_role === 'admin' || current_role === 'owner'

  return (
    <AppLayout>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Avatar
          src={vendor.logo_url || undefined}
          sx={{ width: 64, height: 64, mr: 2, bgcolor: 'primary.main' }}
        >
          {vendor.name.charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4">{vendor.name}</Typography>
          {vendor.description && (
            <Typography variant="body1" color="text.secondary">{vendor.description}</Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isAdmin && (
            <>
              <Button
                variant="outlined"
                startIcon={<PeopleIcon />}
                onClick={() => router.visit(`/vendors/${vendor.slug}/memberships`)}
              >
                Members
              </Button>
              <IconButton onClick={() => router.visit(`/vendors/${vendor.slug}/edit`)}>
                <EditIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">App Entries</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => router.visit(`/vendors/${vendor.slug}/app_entries/new`)}
        >
          New Entry
        </Button>
      </Box>

      {app_entries.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <AppsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No app entries yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first app entry to publish to the store.
          </Typography>
          <Button
            variant="contained"
            onClick={() => router.visit(`/vendors/${vendor.slug}/app_entries/new`)}
          >
            Create First Entry
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {app_entries.map((entry) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={entry.id}>
              <Card elevation={1} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Avatar
                      src={entry.logo_url || undefined}
                      variant="rounded"
                      sx={{ width: 40, height: 40, mr: 1.5, bgcolor: 'grey.200' }}
                    >
                      <AppsIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>{entry.name}</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Chip
                          label={entry.visibility === 'public_entry' ? 'Public' : 'Protected'}
                          size="small"
                          color={entry.visibility === 'public_entry' ? 'success' : 'warning'}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                        {entry.latest_release && (
                          <Chip
                            label={`v${entry.latest_release.version}`}
                            size="small"
                            variant="outlined"
                            color="info"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                  {entry.short_description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {entry.short_description}
                    </Typography>
                  )}
                  {entry.categories.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {entry.categories.map((cat) => (
                        <Chip key={cat.id} label={cat.name} size="small" variant="outlined" />
                      ))}
                    </Box>
                  )}
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    onClick={() => router.visit(`/vendors/${vendor.slug}/app_entries/${entry.slug}/releases`)}
                  >
                    Releases
                  </Button>
                  <Button
                    size="small"
                    onClick={() => router.visit(`/vendors/${vendor.slug}/app_entries/${entry.slug}/edit`)}
                  >
                    Edit
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </AppLayout>
  )
}
